import React from 'react';
import { Box, MenuItem, Switch, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useApiUrl } from '../../../../../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../../../../../global/network/useCampfireFetch';

export type SeekOpportunitySettings = {
  categoryId: number | null;
  commitmentId: number | null;
  email: string;
  locations: string[];
  isOnlineOrRemote: boolean;
};

type SeekLookupOption = { id: number | null; name: string };
type SeekLookupType = 'categories' | 'commitments' | 'locations';

export const EMPTY_SEEK_OPPORTUNITY_SETTINGS: SeekOpportunitySettings = {
  categoryId: null,
  commitmentId: null,
  email: '',
  locations: [],
  isOnlineOrRemote: false,
};

export const getMissingSeekFields = (settings: SeekOpportunitySettings): string[] => {
  const missing = [];
  if (settings.categoryId === null) missing.push('category');
  if (settings.commitmentId === null) missing.push('commitment');
  if (!settings.email.trim()) missing.push('email');
  if (settings.locations.length === 0 && !settings.isOnlineOrRemote) missing.push('location');
  return missing;
};

const useSeekLookup = () => {
  const apiUrl = useApiUrl();
  const lookupFetch = useCampfireFetch<{ options: SeekLookupOption[] }>({ defer: true });

  return (type: SeekLookupType, q?: string): Promise<SeekLookupOption[]> =>
    lookupFetch
      .run({
        url: `${apiUrl}/wm/integrations/seek-volunteer/lookups/get`,
        method: 'get',
        params: { type, q },
      })
      .then((response) => response.data.data.options ?? [])
      .catch(() => []);
};

interface Props {
  value: SeekOpportunitySettings;
  onChange: (value: SeekOpportunitySettings) => void;
  emailPlaceholder?: string;
}

export function SeekOpportunityFields({ value, onChange, emailPlaceholder }: Props) {
  const fetchLookup = useSeekLookup();
  const fetchLocations = useSeekLookup();
  const [categories, setCategories] = React.useState<SeekLookupOption[]>([]);
  const [commitments, setCommitments] = React.useState<SeekLookupOption[]>([]);
  const [locationInput, setLocationInput] = React.useState('');
  const [locationOptions, setLocationOptions] = React.useState<string[]>([]);

  React.useEffect(() => {
    fetchLookup('categories')
      .then(setCategories)
      .then(() => fetchLookup('commitments'))
      .then(setCommitments);
  }, []);

  React.useEffect(() => {
    if (locationInput.trim().length < 2) {
      setLocationOptions([]);
      return undefined;
    }
    const timeout = setTimeout(() => {
      fetchLocations('locations', locationInput).then((options) => setLocationOptions(options.map((o) => o.name)));
    }, 300);
    return () => clearTimeout(timeout);
  }, [locationInput]);

  const update = (patch: Partial<SeekOpportunitySettings>) => onChange({ ...value, ...patch });

  return (
    <Box display='flex' flexDirection='column' style={{ gap: '1rem' }}>
      <TextField
        select
        label='Category'
        variant='outlined'
        size='small'
        value={value.categoryId ?? ''}
        onChange={(e) => update({ categoryId: e.target.value === '' ? null : Number(e.target.value) })}
        fullWidth
      >
        {categories.map((option) => (
          <MenuItem key={option.id ?? option.name} value={option.id ?? ''}>
            {option.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label='Commitment'
        variant='outlined'
        size='small'
        value={value.commitmentId ?? ''}
        onChange={(e) => update({ commitmentId: e.target.value === '' ? null : Number(e.target.value) })}
        fullWidth
      >
        {commitments.map((option) => (
          <MenuItem key={option.id ?? option.name} value={option.id ?? ''}>
            {option.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label='Application notification email'
        variant='outlined'
        size='small'
        type='email'
        value={value.email}
        placeholder={emailPlaceholder}
        InputLabelProps={emailPlaceholder ? { shrink: true } : undefined}
        onChange={(e) => update({ email: e.target.value })}
        fullWidth
      />
      <Autocomplete
        multiple
        freeSolo
        size='small'
        options={locationOptions}
        value={value.locations}
        inputValue={locationInput}
        filterOptions={(options) => options}
        onInputChange={(_unusedEvent, input) => setLocationInput(input)}
        onChange={(_unusedEvent, locations) => update({ locations: locations as string[] })}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Locations'
            variant='outlined'
            placeholder='Search suburb or town'
            helperText='e.g. Melbourne VIC 3000'
          />
        )}
      />
      <Box display='flex' alignItems='center'>
        <Switch
          checked={value.isOnlineOrRemote}
          onChange={() => update({ isOnlineOrRemote: !value.isOnlineOrRemote })}
          color='primary'
        />
        <Typography style={{ fontSize: '14px', fontWeight: 500 }}>Online or remote</Typography>
      </Box>
    </Box>
  );
}
